import React, { useState, useRef } from 'react';
import { DailyScrape, ScraperData } from '../../types';
import { useTheme } from '../../context/ThemeContext';
import { realUrlCounts } from './realScraperData';
interface ScraperGodViewProps {
  scraperData: {
    scrapers: {
      id: number;
      name: string;
    }[];
    dailyData: DailyScrape[];
  };
  viewMode: 'day' | 'week' | 'month';
  colorFilter: string | null;
}
const ScraperGodView: React.FC<ScraperGodViewProps> = ({
  scraperData,
  viewMode,
  colorFilter
}) => {
  const {
    theme
  } = useTheme();
  const {
    dailyData
  } = scraperData;
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    data?: ScraperData;
    time?: 'AM' | 'PM';
    position: {
      top: number;
      left: number;
    };
  }>({
    visible: false,
    position: {
      top: 0,
      left: 0
    }
  });
  // Use all available scrapers from realUrlCounts
  const allScrapers = realUrlCounts;
  // Filter data based on viewMode
  const visibleDays = (() => {
    if (viewMode === 'day') {
      return dailyData.slice(-1); // Only today
    } else if (viewMode === 'week') {
      return dailyData.slice(-7); // Last 7 days
    }
    return dailyData; // Full month (28 days)
  })();
  // Get the background color for the status cell based on delta
  const getCellColor = (data?: ScraperData) => {
    if (!data) {
      return '#7f8aa1'; // Grey for unavailable data
    }
    const status = getStatusFromDelta(data);
    switch (status) {
      case 'broken':
        return '#ff00cc';
      // Pink for broken
      case 'needsAttention':
        return '#ef4444';
      // Red for needs attention
      case 'major':
        return '#f97316';
      // Orange for major
      case 'moderate':
        return '#f59e0b';
      // Amber for moderate
      case 'minor':
        return '#eab308';
      // Yellow for minor
      case 'routine':
        return '#84cc16';
      // Lime green for routine
      case 'minimal':
        return '#22c55e';
      // Green for minimal
      case 'stable':
        return '#4ade80';
      // Light green for stable
      default:
        return '#7f8aa1';
      // Grey for unknown status
    }
  };
  // Get status text from delta
  const getStatusFromDelta = (data?: ScraperData) => {
    if (!data) return 'unavailable';
    const delta = Math.abs(data.delta);
    const percentChange = data.previousUrls ? delta / data.previousUrls * 100 : 0;
    // For broken status (>90% drop)
    if (data.delta < 0 && percentChange >= 90) {
      return 'broken';
    }
    // For other statuses based on absolute change
    if (delta >= 50) {
      return 'needsAttention';
    }
    if (delta >= 40 && delta <= 49) {
      return 'major';
    }
    if (delta >= 30 && delta <= 39) {
      return 'moderate';
    }
    if (delta >= 20 && delta <= 29) {
      return 'minor';
    }
    if (delta >= 10 && delta <= 19) {
      return 'routine';
    }
    if (delta >= 1 && delta <= 9) {
      return 'minimal';
    }
    return 'stable';
  };
  // Check if a cell should be visible based on the color filter
  const isCellVisible = (data?: ScraperData) => {
    if (!data) return colorFilter === '#7f8aa1'; // Only show empty cells if grey filter is selected
    if (!colorFilter) return true; // Show all if no filter is active
    // Get the background color for this cell
    const cellColor = getCellColor(data);
    // Check if it matches the filter exactly
    return cellColor === colorFilter;
  };
  // Handle mouse enter for a cell
  const handleCellMouseEnter = (e: React.MouseEvent, data?: ScraperData, time?: 'AM' | 'PM') => {
    if (!data) return;
    // Get the cell's position for tooltip placement
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect() || {
      top: 0,
      left: 0
    };
    // Calculate position relative to the container
    setTooltipData({
      visible: true,
      data,
      time,
      position: {
        // Position tooltip just slightly above the cell
        top: rect.top - containerRect.top - 15,
        left: rect.left - containerRect.left + rect.width / 2 // Center horizontally
      }
    });
  };
  // Handle mouse leave for a cell
  const handleCellMouseLeave = () => {
    setTooltipData(prev => ({
      ...prev,
      visible: false
    }));
  };
  // Extract ID and Name from combined string
  const extractNameAndId = (scraperName: string) => {
    const nameParts = scraperName.split(' ');
    const id = nameParts[0];
    const name = nameParts.slice(1).join(' ');
    return {
      id,
      name
    };
  };
  // Filter scrapers based on if they have any visible data
  const filteredScrapers = colorFilter ? allScrapers.filter(scraper => {
    return visibleDays.some(day => {
      const amData = day.am.find(d => d.id === scraper.id);
      const pmData = day.pm.find(d => d.id === scraper.id);
      return isCellVisible(amData) || isCellVisible(pmData);
    });
  }) : allScrapers;
  // Calculate cell dimensions
  const cellHeight = 7.5; // Increased from 3 to 7.5 (2.5x taller)
  const cellSpacing = 0; // No spacing between cells
  return <div ref={containerRef} className={`p-4 rounded-lg border w-full relative ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border-gray-200'}`} data-id="element-144">
      <div className="flex items-center justify-between mb-4" data-id="element-145">
        <h3 className="text-sm font-medium" data-id="element-146">Scraper Status Matrix</h3>
        <div className="text-xs" data-id="element-147">
          {visibleDays.length} days × {filteredScrapers.length} scrapers
          {colorFilter && <span className="ml-2 text-fuchsia-400" data-id="element-148">(filtered view)</span>}
        </div>
      </div>

      {/* Matrix container with fixed height */}
      <div className="relative" style={{
      height: `${filteredScrapers.length * (cellHeight + cellSpacing) + 20}px`
    }} data-id="element-149">
        {/* Header row with dates */}
        <div className="flex mb-2 pl-10" data-id="element-150">
          {visibleDays.map((day, dayIndex) => <div key={`header-${day.date}`} className="flex-1 text-center" data-id="element-151">
              <span className="text-[6px] whitespace-nowrap overflow-hidden text-ellipsis" data-id="element-152">
                {day.date}
              </span>
            </div>)}
        </div>

        {/* Main grid */}
        <div className="flex absolute top-6 bottom-0 left-0 right-0" data-id="element-153">
          {/* Left column with scraper IDs */}
          <div className="w-10 flex-shrink-0" data-id="element-154">
            {filteredScrapers.map((scraper, index) => {
            const {
              id
            } = extractNameAndId(scraper.name);
            return <div key={`scraper-${scraper.id}`} className="flex items-center justify-end pr-1" style={{
              height: `${cellHeight}px`,
              marginBottom: `${cellSpacing}px`
            }} data-id="element-155">
                  <span className="text-[6px] truncate" data-id="element-156">{id}</span>
                </div>;
          })}
          </div>

          {/* Main matrix of cells */}
          <div className="flex-grow flex" data-id="element-157">
            {visibleDays.map((day, dayIndex) => <div key={`day-${day.date}`} className="flex-1 flex flex-col" data-id="element-158">
                {filteredScrapers.map((scraper, scraperIndex) => {
              const amData = day.am.find(d => d.id === scraper.id);
              const pmData = day.pm.find(d => d.id === scraper.id);
              const amVisible = isCellVisible(amData);
              const pmVisible = isCellVisible(pmData);
              return <div key={`cell-${scraper.id}-${day.date}`} className="flex" style={{
                height: `${cellHeight}px`,
                marginBottom: `${cellSpacing}px`
              }} data-id="element-159">
                      {/* AM Cell */}
                      <div className={`w-1/2 h-full cursor-pointer relative`} style={{
                  backgroundColor: colorFilter && !amVisible ? '#0F1522' : getCellColor(amData)
                }} onMouseEnter={e => handleCellMouseEnter(e, amData, 'AM')} onMouseLeave={handleCellMouseLeave} data-id="element-160" />
                      {/* PM Cell */}
                      <div className={`w-1/2 h-full cursor-pointer relative`} style={{
                  backgroundColor: colorFilter && !pmVisible ? '#0F1522' : getCellColor(pmData)
                }} onMouseEnter={e => handleCellMouseEnter(e, pmData, 'PM')} onMouseLeave={handleCellMouseLeave} data-id="element-161" />
                    </div>;
            })}
              </div>)}
          </div>
        </div>
      </div>

      {/* Tooltip - positioned absolutely within the container */}
      {tooltipData.visible && tooltipData.data && <div className={`absolute z-50 p-2 rounded-md shadow-lg text-[8px] ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} style={{
      top: `${tooltipData.position.top}px`,
      left: `${tooltipData.position.left}px`,
      transform: 'translate(-50%, -100%)',
      maxWidth: '150px'
    }} data-id="element-162">
          <div className="font-medium mb-1 truncate" data-id="element-163">
            {tooltipData.data.name} - {tooltipData.time}
          </div>
          <div className="grid grid-cols-2 gap-x-1 gap-y-px" data-id="element-164">
            <span className="font-light" data-id="element-165">Status:</span>
            <span className="font-medium" data-id="element-166">
              {getStatusFromDelta(tooltipData.data) === 'needsAttention' ? 'Needs Attention' : getStatusFromDelta(tooltipData.data).charAt(0).toUpperCase() + getStatusFromDelta(tooltipData.data).slice(1)}
            </span>
            <span className="font-light" data-id="element-167">Date:</span>
            <span className="font-medium" data-id="element-168">{tooltipData.data.date}</span>
            <span className="font-light" data-id="element-169">Change:</span>
            <span className={`font-medium ${tooltipData.data.delta > 0 ? 'text-green-500' : tooltipData.data.delta < 0 ? 'text-red-500' : ''}`} data-id="element-170">
              {tooltipData.data.delta > 0 ? '+' : ''}
              {tooltipData.data.delta}
            </span>
          </div>
        </div>}

      {/* Compact Legend */}
      <div className="flex items-center justify-center mt-4 space-x-2 flex-wrap" data-id="element-171">
        <div className="flex items-center" data-id="element-172">
          <span className="w-2 h-2 bg-[#7f8aa1] mr-1" data-id="element-173"></span>
          <span className="text-[7px]" data-id="element-174">N/A</span>
        </div>
        <div className="flex items-center" data-id="element-175">
          <span className="w-2 h-2 bg-[#ff00cc] mr-1" data-id="element-176"></span>
          <span className="text-[7px]" data-id="element-177">Broken</span>
        </div>
        <div className="flex items-center" data-id="element-178">
          <span className="w-2 h-2 bg-[#ef4444] mr-1 border border-red-500" data-id="element-179"></span>
          <span className="text-[7px]" data-id="element-180">Needs Attention</span>
        </div>
        <div className="flex items-center" data-id="element-181">
          <span className="w-2 h-2 bg-[#f97316] mr-1" data-id="element-182"></span>
          <span className="text-[7px]" data-id="element-183">Major</span>
        </div>
        <div className="flex items-center" data-id="element-184">
          <span className="w-2 h-2 bg-[#f59e0b] mr-1" data-id="element-185"></span>
          <span className="text-[7px]" data-id="element-186">Moderate</span>
        </div>
        <div className="flex items-center" data-id="element-187">
          <span className="w-2 h-2 bg-[#eab308] mr-1" data-id="element-188"></span>
          <span className="text-[7px]" data-id="element-189">Minor</span>
        </div>
        <div className="flex items-center" data-id="element-190">
          <span className="w-2 h-2 bg-[#84cc16] mr-1" data-id="element-191"></span>
          <span className="text-[7px]" data-id="element-192">Routine</span>
        </div>
        <div className="flex items-center" data-id="element-193">
          <span className="w-2 h-2 bg-[#22c55e] mr-1" data-id="element-194"></span>
          <span className="text-[7px]" data-id="element-195">Minimal</span>
        </div>
        <div className="flex items-center" data-id="element-196">
          <span className="w-2 h-2 bg-[#4ade80] mr-1" data-id="element-197"></span>
          <span className="text-[7px]" data-id="element-198">Stable</span>
        </div>
      </div>
    </div>;
};
export default ScraperGodView;